import { yupResolver } from '@hookform/resolvers/yup';
import { Cancel01Icon } from '@hugeicons/core-free-icons';
import {
  SessionRole,
  useBulkGetProfiles,
  useCreateSession,
  useGetCanonicalCategoriesByEvent,
  useGetCanonicalTagsByEvent,
  useGetCommunityMembers,
  useGetSession,
  useGetSessionAttendees,
  useGetSessionCategories,
  useGetSessionLocationsByEvent,
  useGetSessionTags,
  useJoinSession,
  useLeaveSession,
  useSearchProfiles,
  useUpdateAttendeeRole,
  useUpdateSession,
  useUpdateSessionCategory,
  useUpdateSessionTags,
} from '@sunrise-ui/api-client';
import { SessionAttendeeRole } from '@sunrise-ui/api/events/api';
import {
  AuthFlowError,
  Button,
  ButtonVariants,
  CollapsibleSection,
  Colors,
  DateTimeInput,
  Dropdown,
  IconButton,
  InputError,
  MultiDropdown,
  SearchAndSelectInput,
  TextInputWithCounter,
  TextReg,
  useAuthStore,
  useDynamicStyles,
  useFormWithUnsavedChanges,
} from '@sunrise-ui/primitives';
import { DateTime } from 'luxon';
import React, { useEffect, useMemo, useState } from 'react';
import { Controller } from 'react-hook-form';
import { KeyboardAvoidingView, Platform, StyleSheet, View } from 'react-native';
import * as yup from 'yup';
import { ProfileResult } from '../profiles/ProfileResult';
import { CreateUserModal } from '../users/CreateUserModal';

const TITLE_MAX_LENGTH = 100;
const DESCRIPTION_MAX_LENGTH = 500;

const sessionSchema = yup.object().shape({
  ownerId: yup.string().required('Please select an owner'),
  title: yup
    .string()
    .max(TITLE_MAX_LENGTH)
    .required('Please enter a session title'),
  description: yup
    .string()
    .max(DESCRIPTION_MAX_LENGTH)
    .required('Please enter a session description'),
  startDateTime: yup.date().optional(),
  endDateTime: yup
    .date()
    .optional()
    .test(
      'is-after-start',
      'End date must be after start date',
      function (endDate) {
        const { startDateTime } = this.parent;
        if (!startDateTime || !endDate) return true;
        return endDate > startDateTime;
      },
    ),
});

type SessionFormData = yup.InferType<typeof sessionSchema>;

type Props = {
  isEdit?: boolean;
  sessionId?: string;
  eventId: string;
  communityId?: string;
  onCancel: () => void;
  onSuccess: () => void;
};

export function SessionForm({
  isEdit = false,
  sessionId,
  eventId,
  communityId,
  onCancel,
  onSuccess,
}: Props) {
  const styles = useDynamicStyles(createStyles);
  const { currentUserId } = useAuthStore();
  const [ownerSearchText, setOwnerSearchText] = useState('');

  // Fetch session data when in edit mode
  const { data: session } = useGetSession(isEdit ? sessionId : undefined);
  const { data: sessionTags } = useGetSessionTags(
    isEdit ? sessionId : undefined,
  );
  const { data: sessionCategories } = useGetSessionCategories(
    isEdit ? sessionId : undefined,
  );
  const { data: attendees, isLoading: isLoadingAttendees } =
    useGetSessionAttendees(isEdit ? sessionId : undefined);

  // Fetch community members for owner dropdown
  const { data: communityMembers } = useGetCommunityMembers(
    communityId || undefined,
    true,
  );
  const memberUserIds = useMemo(
    () => communityMembers?.map(({ user_id }) => user_id) ?? [],
    [communityMembers],
  );
  const { data: memberProfiles } = useBulkGetProfiles(memberUserIds);

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    reset,
    setValue,
    watch,
  } = useFormWithUnsavedChanges<SessionFormData>({
    resolver: yupResolver(sessionSchema),
    defaultValues: {
      ownerId: currentUserId ?? '',
      title: '',
      description: '',
      startDateTime: undefined,
      endDateTime: undefined,
    },
    mode: 'onChange',
  });

  const startDateTime = watch('startDateTime');

  // Unified speakers/moderators with roles
  type WorkingAttendee = { attendeeId?: string; role: SessionRole };
  const [workingAttendees, setWorkingAttendees] = useState<
    Map<string, WorkingAttendee>
  >(new Map());
  const [userSearch, setUserSearch] = useState('');
  const [isCreateUserModalOpen, setIsCreateUserModalOpen] = useState(false);
  const [formInitialized, setFormInitialized] = useState(false);
  const [attendeesInitialized, setAttendeesInitialized] = useState(false);

  // Advanced options state
  const [selectedCategoryId, setSelectedCategoryId] = useState<
    string | undefined
  >();
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [selectedLocationId, setSelectedLocationId] = useState<
    string | undefined
  >();

  // Build current attendee map from server data (for edit mode diff)
  const currentAttendeeMap = useMemo(() => {
    const map = new Map<string, WorkingAttendee>();
    (attendees || [])
      .filter(
        (a) =>
          a.role === SessionRole.SPEAKER || a.role === SessionRole.MODERATOR,
      )
      .forEach((a) => {
        if (a.user_id) {
          map.set(a.user_id, { attendeeId: a.id, role: a.role as SessionRole });
        }
      });
    return map;
  }, [attendees]);

  // Initialize form and state when session data loads (edit mode)
  useEffect(() => {
    if (isEdit && session && !formInitialized) {
      reset({
        ownerId: session.user_id || '',
        title: session.topic_name || '',
        description: session.topic_description || '',
        startDateTime: session.start_date
          ? new Date(session.start_date)
          : undefined,
        endDateTime: session.end_date ? new Date(session.end_date) : undefined,
      });
      setSelectedCategoryId(sessionCategories?.[0]?.category_id || undefined);
      setSelectedTagIds(
        sessionTags
          ?.map(({ tag_id }) => tag_id)
          .filter((id): id is string => Boolean(id)) || [],
      );
      setSelectedLocationId(session.location_id || undefined);
      setFormInitialized(true);
    }
  }, [isEdit, session, sessionCategories, sessionTags, formInitialized, reset]);

  // Initialize attendees when loaded (edit mode)
  useEffect(() => {
    if (isEdit && !attendeesInitialized && !isLoadingAttendees) {
      setWorkingAttendees(new Map(currentAttendeeMap));
      setAttendeesInitialized(true);
    }
  }, [isEdit, attendeesInitialized, isLoadingAttendees, currentAttendeeMap]);

  // Fetch canonical tags, categories, and locations for the event
  const { data: canonicalCategories } =
    useGetCanonicalCategoriesByEvent(eventId);
  const { data: canonicalTags } = useGetCanonicalTagsByEvent(eventId);
  const { data: sessionLocations } = useGetSessionLocationsByEvent(eventId);

  const { mutateAsync: createSession, isPending: isCreatingSession } =
    useCreateSession();
  const { mutateAsync: joinSession, isPending: isJoining } = useJoinSession();
  const { mutateAsync: leaveSession, isPending: isLeaving } = useLeaveSession();
  const { mutateAsync: updateAttendeeRole, isPending: isUpdatingRole } =
    useUpdateAttendeeRole();
  const { mutateAsync: updateSession, isPending: isUpdatingSession } =
    useUpdateSession();
  const { mutateAsync: updateSessionCategory, isPending: isUpdatingCategory } =
    useUpdateSessionCategory();
  const { mutateAsync: updateSessionTags, isPending: isUpdatingTags } =
    useUpdateSessionTags();

  const isSaving =
    isCreatingSession ||
    isJoining ||
    isLeaving ||
    isUpdatingRole ||
    isUpdatingSession ||
    isUpdatingCategory ||
    isUpdatingTags;

  // Search profiles for adding speakers/moderators
  const { data: searchData, isFetching: isSearching } =
    useSearchProfiles(userSearch);
  const searchResults = useMemo(
    () => searchData?.pages.flatMap((p) => p?.results ?? []) || [],
    [searchData],
  );

  const addUserOptions = useMemo(
    () =>
      searchResults
        .filter((p) => !workingAttendees.has(p.user_id))
        .map((p) => ({
          label:
            [p.first_name, p.last_name].filter(Boolean).join(' ') || p.user_id,
          value: p.user_id,
        })),
    [searchResults, workingAttendees],
  );

  // Filter member profiles for owner dropdown based on search text
  const ownerOptions = useMemo(() => {
    if (!memberProfiles) return [];

    const filtered = ownerSearchText
      ? memberProfiles.filter((profile) => {
          const { first_name, last_name, user_id } = profile;
          const fullName = [first_name, last_name]
            .filter(Boolean)
            .join(' ')
            .toLowerCase();
          const searchLower = ownerSearchText.toLowerCase();
          return (
            fullName.includes(searchLower) ||
            user_id.toLowerCase().includes(searchLower)
          );
        })
      : memberProfiles;

    return filtered.map((profile) => ({
      label:
        [profile.first_name, profile.last_name].filter(Boolean).join(' ') ||
        profile.user_id,
      value: profile.user_id,
    }));
  }, [memberProfiles, ownerSearchText]);

  // Dropdown options for advanced settings
  const categoryOptions = useMemo(
    () =>
      canonicalCategories
        ?.filter((cat) => cat.id && cat.name)
        .map((cat) => ({ label: cat.name!, value: cat.id! })) || [],
    [canonicalCategories],
  );

  const tagOptions = useMemo(
    () =>
      canonicalTags
        ?.filter((tag) => tag.id && tag.name)
        .map((tag) => ({ label: tag.name!, value: tag.id! })) || [],
    [canonicalTags],
  );

  const locationOptions = useMemo(
    () =>
      sessionLocations
        ?.filter((loc) => loc.id && loc.name)
        .map((loc) => ({ label: loc.name!, value: loc.id! })) || [],
    [sessionLocations],
  );

  const onSubmit = async (formData: SessionFormData) => {
    const { ownerId, title, description, startDateTime, endDateTime } =
      formData;

    try {
      if (isEdit && sessionId) {
        // ----- EDIT MODE -----
        const currentUsers = new Set(currentAttendeeMap.keys());
        const nextUsers = new Set(workingAttendees.keys());

        // Users to remove
        for (const uid of currentUsers) {
          if (!nextUsers.has(uid)) {
            await leaveSession({ sessionId, userId: uid, eventId });
          }
        }

        // Users to add
        for (const uid of nextUsers) {
          if (!currentUsers.has(uid)) {
            const item = workingAttendees.get(uid)!;
            await joinSession({
              sessionId,
              joinSessionInput: {
                user_id: uid,
                role:
                  item.role === SessionRole.SPEAKER
                    ? SessionAttendeeRole.Speaker
                    : SessionAttendeeRole.Moderator,
              },
              eventId,
            });
          }
        }

        // Users with role change
        for (const uid of currentUsers) {
          if (nextUsers.has(uid)) {
            const current = currentAttendeeMap.get(uid)!;
            const next = workingAttendees.get(uid)!;
            if (
              current.role !== next.role &&
              current.attendeeId &&
              communityId
            ) {
              await updateAttendeeRole({
                sessionId,
                attendeeId: current.attendeeId,
                role:
                  next.role === SessionRole.SPEAKER
                    ? SessionAttendeeRole.Speaker
                    : SessionAttendeeRole.Moderator,
                communityId,
                eventId,
              });
            }
          }
        }

        // Update session details
        const updateData: {
          topic_name?: string;
          topic_description?: string;
          start_date?: string;
          end_date?: string;
          location_id?: string;
        } = {};

        if (title.trim() !== session?.topic_name)
          updateData.topic_name = title.trim();
        if (description.trim() !== session?.topic_description)
          updateData.topic_description = description.trim();
        if (startDateTime)
          updateData.start_date = DateTime.fromJSDate(startDateTime).toISO()!;
        if (endDateTime)
          updateData.end_date = DateTime.fromJSDate(endDateTime).toISO()!;
        if (selectedLocationId !== session?.location_id)
          updateData.location_id = selectedLocationId || undefined;

        if (Object.keys(updateData).length > 0) {
          await updateSession({
            sessionId,
            updateData,
            communityId,
          });
        }

        // Update category if changed
        const currentCategoryId = sessionCategories?.[0]?.category_id;
        if (selectedCategoryId !== currentCategoryId && communityId) {
          await updateSessionCategory({
            sessionId,
            communityId,
            categoryIds: selectedCategoryId ? [selectedCategoryId] : [],
          });
        }

        // Update tags if changed
        const currentTagIds =
          sessionTags
            ?.map(({ tag_id }) => tag_id)
            .filter((id): id is string => Boolean(id))
            .sort()
            .join(',') || '';
        const newTagIds = [...selectedTagIds].sort().join(',');
        if (newTagIds !== currentTagIds && communityId) {
          await updateSessionTags({
            sessionId,
            communityId,
            tagIds: selectedTagIds,
          });
        }

        onSuccess();
      } else {
        // ----- CREATE MODE -----
        const sessionData = {
          event_id: eventId,
          user_id: ownerId,
          topic_name: title.trim(),
          topic_description: description.trim(),
          ...(startDateTime && {
            start_date: DateTime.fromJSDate(startDateTime).toISO()!,
          }),
          ...(endDateTime && {
            end_date: DateTime.fromJSDate(endDateTime).toISO()!,
          }),
        };

        const createdSession = await createSession({
          communityId,
          createSessionInput: sessionData,
        });
        const { id: newSessionId } = createdSession;

        // Add speakers and moderators to the session
        for (const [userId, { role }] of workingAttendees) {
          await joinSession({
            sessionId: newSessionId,
            joinSessionInput: {
              user_id: userId,
              role:
                role === SessionRole.SPEAKER
                  ? SessionAttendeeRole.Speaker
                  : SessionAttendeeRole.Moderator,
            },
          });
        }

        // Update category if selected
        if (selectedCategoryId && communityId) {
          await updateSessionCategory({
            sessionId: newSessionId,
            communityId,
            categoryIds: [selectedCategoryId],
          });
        }

        // Update tags if selected
        if (selectedTagIds.length > 0 && communityId) {
          await updateSessionTags({
            sessionId: newSessionId,
            communityId,
            tagIds: selectedTagIds,
          });
        }

        // Update location if selected
        if (selectedLocationId) {
          await updateSession({
            sessionId: newSessionId,
            updateData: { location_id: selectedLocationId },
            communityId,
          });
        }

        // Reset form
        reset();
        setWorkingAttendees(new Map());
        setUserSearch('');
        setSelectedCategoryId(undefined);
        setSelectedTagIds([]);
        setSelectedLocationId(undefined);

        onSuccess();
      }
    } catch (error) {
      if (error instanceof AuthFlowError) return;

      console.error(
        `Error ${isEdit ? 'updating' : 'creating'} session:`,
        error,
      );
    }
  };

  return (
    <KeyboardAvoidingView
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      style={styles.container}
    >
      <View style={styles.formContainer}>
        <View style={styles.labeledInputGroup}>
          <TextReg>Owner *</TextReg>
          <Controller
            control={control}
            name="ownerId"
            render={({ field: { onChange, value } }) => (
              <SearchAndSelectInput
                placeholder="Search community members..."
                selectedValue={value}
                onSelect={(val) => {
                  onChange(val || '');
                  setOwnerSearchText('');
                }}
                options={ownerOptions}
                isLoading={!memberProfiles}
                searchQuery={ownerSearchText}
                onSearchChange={setOwnerSearchText}
                noResultsText="No community members found"
              />
            )}
          />
          <InputError fieldError={errors.ownerId} />
        </View>

        <Controller
          control={control}
          name="title"
          render={({ field: { onChange, value } }) => (
            <TextInputWithCounter
              label="Title *"
              value={value}
              onChange={onChange}
              placeholder="Enter session title"
              maxLength={TITLE_MAX_LENGTH}
              counterPosition="top"
              fieldError={errors.title}
            />
          )}
        />

        <Controller
          control={control}
          name="description"
          render={({ field: { onChange, value } }) => (
            <TextInputWithCounter
              label="Description *"
              value={value}
              onChange={onChange}
              placeholder="Enter session description"
              multiline
              numberOfLines={6}
              textAlignVertical="top"
              maxLength={DESCRIPTION_MAX_LENGTH}
              counterPosition="top"
              fieldError={errors.description}
            />
          )}
        />

        <View style={styles.labeledInputGroup}>
          <TextReg>Speakers & Moderators</TextReg>
          <View style={styles.speakersSection}>
            {Array.from(workingAttendees.entries()).map(
              ([userId, { role }]) => (
                <View key={userId} style={styles.speakerRow}>
                  <View style={styles.speakerInfo}>
                    <ProfileResult userId={userId} size="small" />
                  </View>
                  <Dropdown<SessionRole>
                    placeholder="Role"
                    options={[
                      { label: 'Speaker', value: SessionRole.SPEAKER },
                      { label: 'Moderator', value: SessionRole.MODERATOR },
                    ]}
                    value={role}
                    onChange={(newRole) => {
                      setWorkingAttendees((prev) => {
                        const next = new Map(prev);
                        const item = next.get(userId);
                        if (item) next.set(userId, { ...item, role: newRole });
                        return next;
                      });
                    }}
                    menuStyle={styles.roleDropdown}
                  />
                  <IconButton
                    icon={Cancel01Icon}
                    onPress={() => {
                      setWorkingAttendees((prev) => {
                        const next = new Map(prev);
                        next.delete(userId);
                        return next;
                      });
                    }}
                    size="small"
                  />
                </View>
              ),
            )}

            <View>
              <SearchAndSelectInput
                placeholder="Search users to add..."
                selectedValue={undefined}
                onSelect={(value) => {
                  if (!value) return;
                  setWorkingAttendees((prev) => {
                    const next = new Map(prev);
                    if (!next.has(value)) {
                      next.set(value, { role: SessionRole.SPEAKER });
                    }
                    return next;
                  });
                }}
                options={addUserOptions}
                isLoading={isSearching}
                searchQuery={userSearch}
                onSearchChange={setUserSearch}
                allowOpenWhenEmpty
                noResultsContent={(closeMenu) => (
                  <Button
                    variant={ButtonVariants.WHITE}
                    size="small"
                    onPress={() => {
                      closeMenu();
                      setIsCreateUserModalOpen(true);
                    }}
                  >
                    Add User
                  </Button>
                )}
              />
              <CreateUserModal
                visible={isCreateUserModalOpen}
                hide={() => setIsCreateUserModalOpen(false)}
                onCreated={(userId) => {
                  setWorkingAttendees((prev) => {
                    const next = new Map(prev);
                    if (!next.has(userId)) {
                      next.set(userId, { role: SessionRole.SPEAKER });
                    }
                    return next;
                  });
                  setIsCreateUserModalOpen(false);
                }}
              />
            </View>
          </View>
        </View>

        <View style={styles.dateTimeRow}>
          <View style={styles.dateTimeGroup}>
            <Controller
              control={control}
              name="startDateTime"
              render={({ field: { onChange, value } }) => (
                <DateTimeInput
                  label="Start"
                  value={value}
                  onChange={(date) => {
                    onChange(date);
                    if (date) {
                      const endDate = DateTime.fromJSDate(date)
                        .plus({ hours: 1 })
                        .toJSDate();
                      setValue('endDateTime', endDate);
                    }
                  }}
                />
              )}
            />
          </View>
          <View style={styles.dateTimeGroup}>
            <Controller
              control={control}
              name="endDateTime"
              render={({ field: { onChange, value } }) => (
                <>
                  <DateTimeInput
                    label="End"
                    value={value}
                    onChange={onChange}
                    minimumDate={startDateTime}
                    error={!!errors.endDateTime}
                  />
                  <InputError fieldError={errors.endDateTime} />
                </>
              )}
            />
          </View>
        </View>

        <CollapsibleSection title="Advanced Settings">
          <Dropdown
            label="Category"
            placeholder="Select category"
            options={categoryOptions}
            value={selectedCategoryId}
            onChange={setSelectedCategoryId}
          />

          <MultiDropdown
            label="Tags"
            placeholder="Select tags"
            options={tagOptions}
            values={selectedTagIds}
            onChange={setSelectedTagIds}
          />

          <Dropdown
            label="Location"
            placeholder="Select location"
            options={locationOptions}
            value={selectedLocationId}
            onChange={setSelectedLocationId}
          />
        </CollapsibleSection>

        <View style={styles.buttonsContainer}>
          <Button
            variant={ButtonVariants.WHITE}
            onPress={onCancel}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            variant={ButtonVariants.PURPLE}
            onPress={handleSubmit(onSubmit)}
            loading={isSaving}
            disabled={!isValid || isSaving}
          >
            Save
          </Button>
        </View>
      </View>
    </KeyboardAvoidingView>
  );
}

const createStyles = () =>
  StyleSheet.create({
    container: {
      flex: 1,
      width: '100%',
    },
    formContainer: {
      width: '100%',
      gap: 16,
    },
    labeledInputGroup: {
      gap: 4,
    },
    dateTimeRow: {
      flexDirection: 'row',
      gap: 16,
    },
    dateTimeGroup: {
      flex: 1,
    },
    buttonsContainer: {
      flexDirection: 'row',
      justifyContent: 'flex-end',
      gap: 12,
    },
    speakersSection: {
      gap: 12,
    },
    speakerRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 12,
      padding: 12,
      backgroundColor: Colors.opacity05,
      borderRadius: 16,
    },
    speakerInfo: {
      flex: 1,
    },
    roleDropdown: {
      width: 160,
    },
  });
